import { DecoratorNode } from 'lexical';

// Vimeo URL patterns to extract video ID
// Supports: vimeo.com/123456789, player.vimeo.com/video/123456789, vimeo.com/channels/*/123456789
const VIMEO_REGEX = /(?:vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)/i;

export function extractVimeoVideoId(url) {
  const match = url.match(VIMEO_REGEX);
  return match ? match[1] : null;
}

export function isVimeoUrl(url) {
  return VIMEO_REGEX.test(url);
}

export class VimeoEmbedNode extends DecoratorNode {
  __videoId;

  static getType() {
    return 'vimeo-embed';
  }

  static clone(node) {
    return new VimeoEmbedNode(node.__videoId, node.__key);
  }

  constructor(videoId, key) {
    super(key);
    this.__videoId = videoId;
  }

  createDOM() {
    const container = document.createElement('div');
    container.className = 'editor-vimeo-container';

    const embedDiv = document.createElement('div');
    embedDiv.className = 'editor-vimeo-embed';
    embedDiv.setAttribute('data-lexical-vimeo', this.__videoId);

    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${this.__videoId}?dnt=1`;
    iframe.frameBorder = '0';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.title = 'Vimeo video';

    embedDiv.appendChild(iframe);
    container.appendChild(embedDiv);

    return container;
  }

  updateDOM(prevNode, dom) {
    // Only update if videoId changed
    if (prevNode.__videoId !== this.__videoId) {
      const iframe = dom.querySelector('iframe');
      if (iframe) {
        iframe.src = `https://player.vimeo.com/video/${this.__videoId}?dnt=1`;
      }
    }
    return false;
  }

  decorate() {
    return null;
  }

  exportDOM() {
    const container = document.createElement('div');
    container.className = 'video-embed vimeo-embed';

    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', `https://player.vimeo.com/video/${this.__videoId}?dnt=1`);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('title', 'Vimeo video');

    container.appendChild(iframe);

    return { element: container };
  }

  static importDOM() {
    return {
      div: (node) => {
        if (!node.classList.contains('vimeo-embed')) {
          return null;
        }
        return {
          conversion: (domNode) => {
            const iframe = domNode.querySelector('iframe');
            if (iframe) {
              const src = iframe.getAttribute('src') || '';
              const videoId = extractVimeoVideoId(src);
              if (videoId) {
                return { node: new VimeoEmbedNode(videoId) };
              }
            }
            return null;
          },
          priority: 1,
        };
      },
    };
  }

  exportJSON() {
    return {
      type: 'vimeo-embed',
      version: 1,
      videoId: this.__videoId,
    };
  }

  static importJSON(json) {
    return new VimeoEmbedNode(json.videoId);
  }

  getVideoId() {
    return this.__videoId;
  }

  isInline() {
    return false;
  }

  isIsolated() {
    return true;
  }

  isKeyboardSelectable() {
    return true;
  }
}

export function $createVimeoEmbedNode(videoId) {
  return new VimeoEmbedNode(videoId);
}

export function $isVimeoEmbedNode(node) {
  return node instanceof VimeoEmbedNode;
}
